import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../components/ui/form.tsx';
import { Input } from '../../../components/ui/input.tsx';
import { Button } from '../../../components/ui/button.tsx';
import { Switch } from '../../../components/ui/switch.tsx';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectLabel,
  SelectGroup,
} from '../../../components/ui/select.tsx';

const formSchema = z.object({
  schoolName: z.string().min(1, 'School name is required'),
  systemLanguage: z.string().min(1, 'System language is required'),
  timeZone: z.string().min(1, 'Time zone is required'),
  currency: z.string().min(1, 'Currency is required'),
  userSignUp: z.boolean(),
  coverPageBackground: z.string().min(1, 'Cover page background is required'),
  dateTimeFormat: z.string().min(1, 'Date and time format is required'),
  notifications: z.boolean(),
  activeUsers: z.number().min(1, 'Active users must be at least 1'),
  updateFrequency: z.string().min(1, 'Update frequency is required'),
  fileFormat: z.string().min(1, 'File format is required'),
});

const General = () => {
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      schoolName: '',
      systemLanguage: '',
      timeZone: '',
      currency: '',
      userSignUp: false,
      coverPageBackground: '',
      dateTimeFormat: '',
      notifications: false,
      activeUsers: 1,
      updateFrequency: '',
      fileFormat: '',
    },
  });

  return (
    <div className="md:p-5 p-1  rounded-md bg-white">
      <div className="">
        <p className="text-lg font-semibold">General</p>
        <Form {...form}>
          <form action="#">
            {form.formState.errors.root && (
              <div className="p-3 text-sm text-red-600 bg-red-50 rounded-md">
                {form.formState.errors.root.message}
              </div>
            )}
            <div className="mt-4 grid md:grid-cols-3 grid-cols-1 gap-x-12 text-[#404651]">
              <div className="w-full">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-24 h-24 border border-black rounded-full" />
                  <div className="text-sm">
                    <p className="underline">Edit School's Avatar</p>
                    <p className="text-xs text-[#7E8899]">
                      Last updated: 12/05/2024
                    </p>
                  </div>
                </div>
                <FormField
                  control={form.control}
                  name="schoolName"
                  render={({ field }) => (
                    <FormItem className="mb-6">
                      <FormLabel>School Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Manabu Academy" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem className="mb-6">
                      <FormLabel>System Language</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a supported language" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="English">English</SelectItem>
                          <SelectItem value="Spanish">Spanish</SelectItem>
                          <SelectItem value="French">French</SelectItem>
                        </SelectContent>
                      </Select>

                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem className="mb-6">
                      <FormLabel>Time Zone</FormLabel>
                      <Select>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a timezone" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            <SelectLabel>North America</SelectLabel>
                            <SelectItem value="est">
                              Eastern Standard Time (EST)
                            </SelectItem>
                            <SelectItem value="cst">
                              Central Standard Time (CST)
                            </SelectItem>
                            <SelectItem value="mst">
                              Mountain Standard Time (MST)
                            </SelectItem>
                            <SelectItem value="pst">
                              Pacific Standard Time (PST)
                            </SelectItem>
                            <SelectItem value="akst">
                              Alaska Standard Time (AKST)
                            </SelectItem>
                            <SelectItem value="hst">
                              Hawaii Standard Time (HST)
                            </SelectItem>
                          </SelectGroup>
                          <SelectGroup>
                            <SelectLabel>Europe & Africa</SelectLabel>
                            <SelectItem value="gmt">
                              Greenwich Mean Time (GMT)
                            </SelectItem>
                            <SelectItem value="cet">
                              Central European Time (CET)
                            </SelectItem>
                            <SelectItem value="eet">
                              Eastern European Time (EET)
                            </SelectItem>
                            <SelectItem value="west">
                              Western European Summer Time (WEST)
                            </SelectItem>
                            <SelectItem value="cat">
                              Central Africa Time (CAT)
                            </SelectItem>
                            <SelectItem value="eat">
                              East Africa Time (EAT)
                            </SelectItem>
                          </SelectGroup>
                          <SelectGroup>
                            <SelectLabel>Asia</SelectLabel>
                            <SelectItem value="msk">
                              Moscow Time (MSK)
                            </SelectItem>
                            <SelectItem value="ist">
                              India Standard Time (IST)
                            </SelectItem>
                            <SelectItem value="cst_china">
                              China Standard Time (CST)
                            </SelectItem>
                            <SelectItem value="jst">
                              Japan Standard Time (JST)
                            </SelectItem>
                            <SelectItem value="kst">
                              Korea Standard Time (KST)
                            </SelectItem>
                            <SelectItem value="ist_indonesia">
                              Indonesia Central Standard Time (WITA)
                            </SelectItem>
                          </SelectGroup>
                          <SelectGroup>
                            <SelectLabel>Australia & Pacific</SelectLabel>
                            <SelectItem value="awst">
                              Australian Western Standard Time (AWST)
                            </SelectItem>
                            <SelectItem value="acst">
                              Australian Central Standard Time (ACST)
                            </SelectItem>
                            <SelectItem value="aest">
                              Australian Eastern Standard Time (AEST)
                            </SelectItem>
                            <SelectItem value="nzst">
                              New Zealand Standard Time (NZST)
                            </SelectItem>
                            <SelectItem value="fjt">Fiji Time (FJT)</SelectItem>
                          </SelectGroup>
                          <SelectGroup>
                            <SelectLabel>South America</SelectLabel>
                            <SelectItem value="art">
                              Argentina Time (ART)
                            </SelectItem>
                            <SelectItem value="bot">
                              Bolivia Time (BOT)
                            </SelectItem>
                            <SelectItem value="brt">
                              Brasilia Time (BRT)
                            </SelectItem>
                            <SelectItem value="clt">
                              Chile Standard Time (CLT)
                            </SelectItem>
                          </SelectGroup>
                        </SelectContent>
                      </Select>

                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="currency"
                  render={({ field }) => (
                    <FormItem className="mb-6 md:mb-0">
                      <FormLabel>Currency</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a currency" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="NGN">
                            Nigerian Naira (NGN)
                          </SelectItem>
                          <SelectItem value="USD">US Dollar (USD)</SelectItem>
                          <SelectItem value="EUR">Euro (EUR)</SelectItem>
                          <SelectItem value="GBP">
                            British Pound (GBP)
                          </SelectItem>
                        </SelectContent>
                      </Select>

                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div>
                <FormField
                  control={form.control}
                  name="user_signup"
                  render={({ field }) => (
                    <FormItem className="mb-6">
                      <FormLabel>User Sign up</FormLabel>
                      <div className="flex flex-row items-center justify-between border-t border-t-[#BBD2EC] pt-2">
                        <FormDescription>
                          Allow new users to sign up.
                        </FormDescription>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </div>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="theme"
                  render={({ field }) => (
                    <FormItem className="mb-6">
                      <FormLabel>Theme</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a Theme" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="Light">Light Theme</SelectItem>
                          <SelectItem value="Dark">Dark Theme</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="date_format"
                  render={({ field }) => (
                    <FormItem className="mb-6">
                      <FormLabel>Date and Time Format</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a Format" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="dmy">DD/MM/YYYY</SelectItem>
                          <SelectItem value="mdy">MM/DD/YYYY</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="notifications"
                  render={({ field }) => (
                    <FormItem className="mb-6 md:mb-0">
                      <FormLabel>Notifications</FormLabel>
                      <div className="flex flex-row items-center justify-between border-t border-t-[#BBD2EC] pt-2">
                        <FormDescription>
                          Allow system notifications
                        </FormDescription>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </div>
                    </FormItem>
                  )}
                />
              </div>
              <div>
                <FormField
                  control={form.control}
                  name="active_users"
                  render={({ field }) => (
                    <FormItem className="mb-6">
                      <FormLabel>Active Session</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select an active session" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="Light">
                            2022/2023 Session
                          </SelectItem>
                          <SelectItem value="Dark">
                            2023/2024 Session
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="frequency"
                  render={({ field }) => (
                    <FormItem className="mb-6">
                      <FormLabel>System Update Frequency</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a Frequency" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="Daily">Daily</SelectItem>
                          <SelectItem value="Weekly">Weekly</SelectItem>
                          <SelectItem value="Monthly">Monthly</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="file_format"
                  render={({ field }) => (
                    <FormItem className="mb-6 md:mb-0">
                      <FormLabel>
                        Logs/Reports File Format for Download
                      </FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a File Format" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="PDF">PDF</SelectItem>
                          <SelectItem value="DOCX">DOCX</SelectItem>
                          <SelectItem value="CSV">CSV</SelectItem>
                          <SelectItem value="XLSX">XLSX</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />
              </div>
            </div>
            <div className="flex gap-x-4 mt-5 w-full justify-end px-5">
              <Button>Save</Button>
              <Button variant="destructive">Cancel</Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
};

export default General;
